import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Bot, Search, RefreshCw, Loader2, Calendar, User,
  TrendingUp, TrendingDown, CheckCircle, XCircle, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const AdminStrategies = () => {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/strategies');
      setStrategies(response.data || []);
    } catch (error) {
      console.error('Strategies fetch error:', error);
      toast.error('Stratejiler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return '-';
    }
  };

  const filteredStrategies = strategies.filter(s => 
    !searchQuery || 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalStrategies = strategies.length;
  const activeStrategies = strategies.filter(s => s.is_active).length;
  const publicStrategies = strategies.filter(s => s.is_public).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            Strateji Yönetimi
          </h1>
          <p className="text-slate-400 text-sm">Tüm stratejileri görüntüle ve yönet</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStrategies}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Yenile
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Toplam</p>
                <p className="text-2xl font-bold text-blue-400">{totalStrategies}</p>
              </div>
              <Bot className="h-8 w-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Aktif</p>
                <p className="text-2xl font-bold text-green-400">{activeStrategies}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Pazaryerinde</p>
                <p className="text-2xl font-bold text-primary">{publicStrategies}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          placeholder="Strateji adı veya kullanıcı ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-brand-dark border-border/40"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Stratejiler ({filteredStrategies.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredStrategies.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Bot className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>Strateji bulunamadı</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredStrategies.map((strategy) => (
                <div key={strategy.id} className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{strategy.name}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {strategy.user_email || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(strategy.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {strategy.is_active ? (
                      <Badge className="bg-green-500/20 text-green-400">Aktif</Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500">Pasif</Badge>
                    )}
                    {strategy.is_public && (
                      <Badge className="bg-primary/20 text-primary">Pazaryeri</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStrategies;
